import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';

interface InAppBrowserOptions {
  url: string;
  windowName?: string;
  presentationStyle?: 'fullscreen' | 'popover';
  toolbarColor?: string;
}

export const useInAppBrowser = () => {
  const openInAppBrowser = async ({ url, windowName = '_blank', presentationStyle = 'fullscreen', toolbarColor }: InAppBrowserOptions) => {
    try {
      // Always check for native platform first
      if (Capacitor.isNativePlatform()) {
        console.log('[useInAppBrowser] Opening in Safari View Controller on native platform');
        
        // Ensure Browser plugin is available
        if (!Browser?.open) {
          console.error('[useInAppBrowser] Capacitor Browser plugin not available');
          throw new Error('Browser plugin not available on native platform');
        }

        // Use Safari View Controller on iOS/Android - no fallback to external browser
        await Browser.open({
          url,
          windowName,
          presentationStyle,
          toolbarColor: toolbarColor || '#000000',
        });
      } else {
        // Only use window.open for actual web platforms
        console.log('[useInAppBrowser] Opening in new tab on web platform');
        window.open(url, windowName);
      }
    } catch (error) {
      console.error('[useInAppBrowser] Error opening browser:', error);
      
      // For native platforms, don't fall back to window.open as it opens external browser
      if (Capacitor.isNativePlatform()) {
        throw error; // Re-throw to handle properly in calling code
      } else {
        // Only fallback to window.open on web
        window.open(url, windowName);
      }
    }
  };

  const closeInAppBrowser = async () => {
    try {
      if (Capacitor.isNativePlatform() && Browser?.close) {
        await Browser.close();
      }
    } catch (error) {
      console.error('[useInAppBrowser] Error closing browser:', error);
    }
  };

  return {
    openInAppBrowser,
    closeInAppBrowser,
  };
};